import { API } from "@swivl-mono/api-helper";
import { filterClasses } from "@swivl-mono/helpers";
import {
  BOT_SEQUENCE_SUPPRESSED_EVENTS_DISPLAY_TIMEZONE,
  BotSequenceActiveSuppressionsResponse,
  BotSequenceActiveSuppressionsSortKey,
  BotSequenceEventStats,
  BotSequenceEventsResponse,
  BotSequenceEventsSortKey,
  BotSequenceScheduledAttemptStatus,
  BotSequenceSuppressedEventStats,
  BotSequenceSuppressedOutcome,
  BotSequenceType
} from "@swivl-mono/types";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  DataTable,
  DataTableVariant,
  DropdownOptionItem,
  DropdownOptionVariant,
  FilterDropdown,
  Panel,
  SearchInput
} from "@swivl-mono/ui";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ActiveFilterChipSection, ActiveFilterChipSectionVariant } from "../../../Components/ActiveFilterChipSection/ActiveFilterChipSection";
import { buildCRMSearchParams, parseOptionalNumber } from "../../../Helpers/CRM/searchParams.Helper";
import useDebounce from "../../../Hooks/useDebounce";
import { usePropertiesGetLocationIds } from "../../../Hooks/usePropertiesGetLocationIds";
import { useUrlState } from "../../../Hooks/useUrlState";
import LocationsModel from "../../../Models/Locations/Locations.Model";
import { Member } from "../../../Models/Member/Member.Current";
import { NavBar } from "../../../Models/NavBar/NavBar.Model";
import { AlertsModel } from "../../../Models/Notifications/Alerts.Model";
import { LocationsListComponent } from "../../Properties/Components/Locations.List.Component";
import { CRMNavContent } from "../Components/CRM.NavTabs";
import { SuppressionsDetailPanel } from "../SequenceSuppressions/SequenceSuppressions.DetailPanel";
import {
  REASON_DROPDOWN_GROUPS,
  SEQUENCE_LABELS,
  SEQUENCE_ORDER,
  SuppressionsViewLevel,
  decodeReasonFilter,
  encodeReasonFilter,
  extractErrorReason,
  formatInstant,
  formatSubject,
  formatUser,
  loadViewLevel,
  pagerLabel,
  storeViewLevel
} from "../SequenceSuppressions/SequenceSuppressions.Shared";
import { SUPPRESSIONS_SORTABLE_COLUMNS, SuppressionsTab } from "../SequenceSuppressions/SequenceSuppressions.SuppressionsTab";
import { ByReasonCard } from "./SequenceEvents.ByReasonCard";
import { SequenceEventDetailPanel } from "./SequenceEvents.DetailPanel";
import { DeliveriesCell, STATUS_DROPDOWN_GROUPS, STATUS_TILES, StatusChip, describeMissedReason } from "./SequenceEvents.Shared";

type TabKey = "events" | "suppressions";

const VIEW_TABS: [TabKey, string][] = [
  ["events", "Events"],
  ["suppressions", "Suppressions"]
];

/** How long a keystroke in the user box waits before it becomes a query. */
const USER_FILTER_DEBOUNCE_MS = 400;

/**
 * The columns the server sorts on (ENG-3810); ids match the server's keys. "Attempted" is not
 * here on purpose: it is a per-channel list with no single value to order by.
 */
const SORTABLE_COLUMNS = new Set<BotSequenceEventsSortKey>([
  "localDay",
  "occurredAt",
  "locationName",
  "userName",
  "subject",
  "stepOffset",
  "status",
  "reason"
]);

/** The Not sent tile's statuses — the slice that carries reasons. */
const NOT_SENT_STATUSES: BotSequenceScheduledAttemptStatus[] = STATUS_TILES.find((tile) => tile.key === "notSent")?.statuses ?? [];

/**
 * Every filter group the dropdown and the chips show: the ledger statuses, then the not-sent
 * reasons by family. The static reason list is a convenience; a reason the data carries that the
 * list does not know (a sweeper's, a test run's) is added from the By reason stats so its chip
 * reads the reason, not the raw `outcome::reason` key.
 */
function buildFilterGroups(reasonStats: BotSequenceSuppressedEventStats | null): DropdownOptionItem[] {
  const reasonGroups = REASON_DROPDOWN_GROUPS.map((group) => {
    const options = group.options ?? [];
    const known = new Set(options.map((option) => option.value));
    const extra = (reasonStats?.reasons ?? [])
      .filter((row) => row.outcome === group.value && !known.has(encodeReasonFilter(row.outcome, row.reason)))
      .map((row) => ({ value: encodeReasonFilter(row.outcome, row.reason), label: row.reason, variant: DropdownOptionVariant.WITH_CHECKBOX }));
    return extra.length > 0 ? { ...group, options: [...options, ...extra] } : group;
  });
  return [...STATUS_DROPDOWN_GROUPS, ...reasonGroups];
}

/**
 * The default `localDay` range — yesterday + today, derived in the page's fixed display
 * zone (the same default the server resolves when the bounds are omitted).
 */
function defaultDateRange(): { from: string; to: string } {
  const today = DateTime.now().setZone(BOT_SEQUENCE_SUPPRESSED_EVENTS_DISPLAY_TIMEZONE);
  return { from: today.minus({ days: 1 }).toISODate() as string, to: today.toISODate() as string };
}

function sameStatuses(a: BotSequenceScheduledAttemptStatus[], b: BotSequenceScheduledAttemptStatus[]): boolean {
  return a.length === b.length && a.every((status) => b.includes(status));
}

/**
 * CRM Sequences (ENG-2990) — "what is the Sequences engine doing here?": every scheduling
 * decision in the ledger, ANY status, filterable by status / date / user and sortable
 * server-side. It absorbed the former Sequence Suppressions page (ENG-2862 → ENG-3811, Chuck,
 * 2026-10-22): the "By reason" breakdown appears while the Not sent tile is selected, and the
 * enrollments on hold right now live on the Suppressions tab. One page, one ledger.
 *
 * Same CRM surface conventions as before: the left sidebar is the org/group/location
 * selector and the ONLY location scoping; the data is scoped to the member's org; a clicked
 * row opens the detail in a right-side panel; one sequence type at a time with the segment
 * badges carrying the cross-sequence signal; derived times render in a fixed
 * America/New_York while `localDay` / `anchorDate` are shown verbatim.
 */
export function SequenceEventsView(): React.ReactElement {
  const orgId = Member.orgId!;
  const page = usePropertiesGetLocationIds();
  const { searchParams, setSearchParam } = useUrlState();

  // The CRM sidebar's selection, shared through the same search params as /crm — the
  // sidebar IS this page's location filter (`none` = the "No Location" entry).
  const rawScopeGroupId = searchParams.get("groupId");
  const rawScopeLocationId = searchParams.get("locationId");
  const scopeGroupId: number | "none" | undefined = rawScopeGroupId === "none" ? "none" : parseOptionalNumber(rawScopeGroupId);
  const scopeLocationId: number | "none" | undefined = rawScopeLocationId === "none" ? "none" : parseOptionalNumber(rawScopeLocationId);
  const userParam = searchParams.get("user") ?? "";
  // Deep links: `tab=suppressions` (the former Suppressions page's route) and `notSent=1`
  // (the CRM user card's "Sequence suppressions" link) land on what they used to show.
  const tabParam: TabKey = searchParams.get("tab") === "suppressions" ? "suppressions" : "events";
  const notSentParam = searchParams.get("notSent") === "1";

  const [sequenceType, setSequenceType] = useState<BotSequenceType>(BotSequenceType.PAST_DUE);
  const [tab, setTab] = useState<TabKey>(tabParam);
  const [from, setFrom] = useState(() => defaultDateRange().from);
  const [to, setTo] = useState(() => defaultDateRange().to);
  const [statuses, setStatuses] = useState<BotSequenceScheduledAttemptStatus[]>(() => (notSentParam ? NOT_SENT_STATUSES : []));
  const [drill, setDrill] = useState<{ outcome: BotSequenceSuppressedOutcome | null; reason: string | null }>({ outcome: null, reason: null });
  const [userInput, setUserInput] = useState(userParam);
  const { debouncedValue: debouncedUserInput } = useDebounce<string>(userInput, USER_FILTER_DEBOUNCE_MS);
  const [sorting, setSorting] = useState<SortingState>([{ id: "localDay", desc: true }]);
  const [suppressionsSorting, setSuppressionsSorting] = useState<SortingState>([{ id: "suppressUntil", desc: false }]);
  const [eventsPage, setEventsPage] = useState(1);
  const [suppressionsPage, setSuppressionsPage] = useState(1);
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  const [selectedEnrollmentId, setSelectedEnrollmentId] = useState<number | null>(null);
  const [viewLevel, setViewLevel] = useState<SuppressionsViewLevel>(() => loadViewLevel(Member.isSwivlStaff));

  const [feed, setFeed] = useState<BotSequenceEventsResponse | null>(null);
  const [stats, setStats] = useState<BotSequenceEventStats | null>(null);
  const [reasonStats, setReasonStats] = useState<BotSequenceSuppressedEventStats | null>(null);
  const [suppressions, setSuppressions] = useState<BotSequenceActiveSuppressionsResponse | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    LocationsModel.getGroupsAndLocations(false);
    NavBar.set(<CRMNavContent />, null, null, "Sequences");
  }, []);

  // The committed user filter follows the debounced input; external changes to the URL
  // param (a deep link carrying `user`) sync back into the box.
  useEffect(() => {
    if (debouncedUserInput !== userParam) {
      setSearchParam("user", debouncedUserInput || undefined);
      resetPagingAndSelection();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedUserInput]);
  useEffect(() => {
    setUserInput(userParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userParam]);

  // A sidebar scope change invalidates the narrower filters (CRM clears its filters the
  // same way). The user filter survives — it arrives together with the scope on deep links.
  const isFirstScopeRun = useRef(true);
  useEffect(() => {
    if (isFirstScopeRun.current) {
      isFirstScopeRun.current = false;
      return;
    }
    setStatuses([]);
    setDrill({ outcome: null, reason: null });
    resetPagingAndSelection();
  }, [scopeLocationId, scopeGroupId]);

  // Location scoping comes from the sidebar alone: org → all locations, group → its
  // locations, location → exactly that location, "No Location" → rows with no location.
  const effectiveLocationId = scopeLocationId;
  const effectiveGroupId = scopeLocationId !== undefined ? undefined : scopeGroupId;

  const sharedParams = useMemo(
    () => ({
      sequenceType,
      groupId: effectiveGroupId,
      locationId: effectiveLocationId,
      user: userParam || undefined
    }),
    [sequenceType, effectiveGroupId, effectiveLocationId, userParam]
  );
  const rangeParams = useMemo(() => ({ from: from || undefined, to: to || undefined }), [from, to]);

  const sortParams = useMemo(() => {
    const active = sorting[0];
    if (!active || !SORTABLE_COLUMNS.has(active.id as BotSequenceEventsSortKey)) {
      return {};
    }
    return { sortBy: active.id as BotSequenceEventsSortKey, sortOrder: active.desc ? ("DESC" as const) : ("ASC" as const) };
  }, [sorting]);

  const suppressionsSortParams = useMemo(() => {
    const active = suppressionsSorting[0];
    if (!active || !SUPPRESSIONS_SORTABLE_COLUMNS.has(active.id as BotSequenceActiveSuppressionsSortKey)) {
      return {};
    }
    return { sortBy: active.id as BotSequenceActiveSuppressionsSortKey, sortOrder: active.desc ? ("DESC" as const) : ("ASC" as const) };
  }, [suppressionsSorting]);

  // The reason breakdown belongs to the not-sent slice: it is fetched and shown only while
  // the Not sent tile is the status filter (or a reason drill is active).
  const showByReason = sameStatuses(statuses, NOT_SENT_STATUSES) || drill.reason !== null;

  const loadEvents = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      // The badges cover all four types and the tiles all statuses, so stats refresh with
      // the feed rather than only when the segment changes. The reason drill rides the feed
      // and the reason breakdown (which follows it); the status stats ignore it.
      const drillParams = { outcome: drill.outcome ?? undefined, reason: drill.reason ?? undefined };
      const [feedResponse, statsResponse, reasonResponse] = await Promise.all([
        API.Sequences.getSequenceEvents(orgId, {
          ...sharedParams,
          ...rangeParams,
          ...sortParams,
          ...drillParams,
          statuses: statuses.length > 0 ? statuses : undefined,
          page: eventsPage
        }),
        API.Sequences.getSequenceEventStats(orgId, { ...sharedParams, ...rangeParams }),
        showByReason ? API.Sequences.getSuppressedEventStats(orgId, { ...sharedParams, ...rangeParams, ...drillParams }) : Promise.resolve(null)
      ]);
      setFeed(feedResponse);
      setStats(statsResponse);
      setReasonStats(reasonResponse);
    } catch (error) {
      AlertsModel.errorToast(extractErrorReason(error));
    } finally {
      setLoading(false);
    }
  }, [orgId, sharedParams, rangeParams, sortParams, drill.outcome, drill.reason, statuses, eventsPage, showByReason]);

  const loadSuppressions = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      // This tab's badges come from the response's own byType (currently-held counts).
      const suppressionsResponse = await API.Sequences.getActiveSuppressions(orgId, {
        ...sharedParams,
        ...suppressionsSortParams,
        page: suppressionsPage
      });
      setSuppressions(suppressionsResponse);
    } catch (error) {
      AlertsModel.errorToast(extractErrorReason(error));
    } finally {
      setLoading(false);
    }
  }, [orgId, sharedParams, suppressionsSortParams, suppressionsPage]);

  useEffect(() => {
    if (tab === "events") {
      void loadEvents();
    } else {
      void loadSuppressions();
    }
  }, [tab, loadEvents, loadSuppressions]);

  const resetPagingAndSelection = (): void => {
    setEventsPage(1);
    setSuppressionsPage(1);
    setSelectedEventId(null);
    setSelectedEnrollmentId(null);
  };

  const handleSequenceTypeChange = (next: BotSequenceType): void => {
    // Re-clicking the active segment is a no-op — no paging reset, no new request.
    if (next === sequenceType) {
      return;
    }
    setSequenceType(next);
    resetPagingAndSelection();
  };

  const handleTabChange = (next: TabKey): void => {
    if (next === tab) {
      return;
    }
    setTab(next);
    setSearchParam("tab", next === "suppressions" ? "suppressions" : undefined);
    resetPagingAndSelection();
  };

  /**
   * One handler for both dropdown groups: the `status` group sets the status filter; a
   * reason group (keyed by its outcome family) sets the reason drill, which implies the
   * Not sent slice so the breakdown stays visible.
   */
  const handleFilterChange = (groupKey: string, filters: string[]): void => {
    if (groupKey === "status" || groupKey === "") {
      setStatuses(filters as BotSequenceScheduledAttemptStatus[]);
      if (filters.length === 0) setDrill({ outcome: null, reason: null });
    } else {
      const decoded = filters[0] ? decodeReasonFilter(filters[0]) : null;
      setDrill({ outcome: decoded?.outcome ?? null, reason: decoded?.reason ?? null });
      if (decoded) setStatuses(NOT_SENT_STATUSES);
    }
    resetPagingAndSelection();
  };

  const handleClearFilters = (): void => {
    const range = defaultDateRange();
    setStatuses([]);
    setDrill({ outcome: null, reason: null });
    setFrom(range.from);
    setTo(range.to);
    setUserInput("");
    setSearchParam("user", undefined);
    setSearchParam("notSent", undefined);
    resetPagingAndSelection();
  };

  /** A tile drills into its status family; re-clicking the active tile clears the drill (and any reason with it). */
  const handleTileClick = (tileStatuses: BotSequenceScheduledAttemptStatus[] | null): void => {
    const next = tileStatuses ?? [];
    const isSame = sameStatuses(next, statuses);
    setStatuses(isSame ? [] : next);
    setDrill({ outcome: null, reason: null });
    resetPagingAndSelection();
  };

  /** A row of the By reason card: drill the feed to that reason (a second click clears it). */
  const handleReasonDrill = (outcome: BotSequenceSuppressedOutcome | null, reason: string | null): void => {
    setDrill({ outcome, reason });
    resetPagingAndSelection();
  };

  const handleSortingChange = (next: SortingState): void => {
    setSorting(next);
    resetPagingAndSelection();
  };

  const handleSuppressionsSortingChange = (next: SortingState): void => {
    setSuppressionsSorting(next);
    resetPagingAndSelection();
  };

  const handleViewLevelChange = (level: SuppressionsViewLevel): void => {
    setViewLevel(level);
    storeViewLevel(level);
  };

  /** From a row's detail: this page, narrowed to the user's not-sent events. */
  const handleViewUserNotSent = (userId: number): void => {
    setUserInput(`#${userId}`);
    setSearchParam("user", `#${userId}`);
    setStatuses(NOT_SENT_STATUSES);
    setDrill({ outcome: null, reason: null });
    setTab("events");
    setSearchParam("tab", undefined);
    resetPagingAndSelection();
  };

  const filterGroups = useMemo(() => buildFilterGroups(reasonStats), [reasonStats]);

  const selectedFilters = useMemo<Record<string, string[]>>(() => {
    const filters: Record<string, string[]> = {};
    if (statuses.length > 0) {
      filters.status = statuses;
    }
    if (drill.outcome && drill.reason) {
      filters[drill.outcome] = [encodeReasonFilter(drill.outcome, drill.reason)];
    }
    return filters;
  }, [statuses, drill.outcome, drill.reason]);

  const events = feed?.events ?? [];
  const total = feed?.total ?? 0;
  const feedPage = feed?.page ?? 1;
  const pageSize = feed?.pageSize ?? 50;
  const lastPage = total > 0 ? Math.ceil(total / pageSize) : 1;

  // DataTable keys rows by `id`; the detail panel keys off the same attempt id.
  const rows = useMemo(() => events.map((event) => ({ ...event, id: event.attemptId })), [events]);

  const columns: ColumnDef<(typeof rows)[number]>[] = useMemo(
    () => [
      // localDay is the location's own business day — shown verbatim, never converted.
      { header: "Day", accessorKey: "localDay", enableSorting: true, cell: (info) => <span className="font-mono">{String(info.getValue())}</span> },
      { header: "Time", accessorKey: "occurredAt", enableSorting: true, cell: (info) => formatInstant(info.getValue() as string) },
      { header: "Location", accessorKey: "locationName", enableSorting: true, cell: (info) => (info.getValue() as string | null) ?? "—" },
      {
        header: "User",
        accessorKey: "userName",
        enableSorting: true,
        cell: (info) => formatUser(info.getValue() as string | null, info.row.original.userId)
      },
      {
        header: "Subject",
        id: "subject",
        accessorKey: "leaseId",
        enableSorting: true,
        cell: (info) => formatSubject(info.row.original.leaseId, info.row.original.leadId)
      },
      { header: "Step", accessorKey: "stepOffset", enableSorting: true, cell: (info) => `day ${info.getValue()}` },
      {
        // A per-channel list — nothing single to sort by, so no sort affordance (ENG-3810).
        header: "Attempted",
        accessorKey: "deliveries",
        enableSorting: false,
        cell: (info) => <DeliveriesCell deliveries={info.row.original.deliveries} />
      },
      {
        header: "Status",
        accessorKey: "status",
        enableSorting: true,
        cell: (info) => <StatusChip status={info.row.original.status} reason={info.row.original.reason} />
      },
      {
        header: "Reason",
        accessorKey: "reason",
        enableSorting: true,
        cell: (info) => (
          <span>
            {describeMissedReason(info.getValue() as string | null, info.row.original.missedOffsets, info.row.original.stepOffset) ?? "—"}
            {info.row.original.errorDetail && <span className="ml-1 text-red-700">⚠</span>}
          </span>
        )
      }
    ],
    []
  );

  const selectedEvent = selectedEventId !== null ? (events.find((event) => event.attemptId === selectedEventId) ?? null) : null;
  const selectedSuppression =
    selectedEnrollmentId !== null ? (suppressions?.rows.find((row) => row.enrollmentId === selectedEnrollmentId) ?? null) : null;
  const isEventsTab = tab === "events";
  const timezoneLabel = BOT_SEQUENCE_SUPPRESSED_EVENTS_DISPLAY_TIMEZONE.replace("_", " ");

  return (
    <>
      <div className="flex size-full divide-x divide-grey-200">
        <div className="w-[300px] shrink-0 overflow-y-auto bg-white">
          <LocationsListComponent
            {...page}
            prependLinks={[
              { name: "No Location", url: { search: buildCRMSearchParams({ level: "location", locationId: "none", groupId: "none" }, searchParams) } }
            ]}
          />
        </div>
        <div className="flex h-full min-w-0 flex-1 flex-col">
          {/* The view — the outer choice (Chuck, 2026-10-22): the ledger of decisions, or the
              enrollments on hold. Everything below the strip belongs to the chosen view; the
              filters and sequence buttons narrow whichever is showing. Same underline-tab look as
              the Contact Center channel tabs. Same height, background and rule as the org row at
              the top of the location column, so the two meet in a straight line. */}
          <div className="relative flex h-[55px] shrink-0 items-center border-b border-grey-500 bg-grey-50 px-3 text-sm font-semibold">
            {VIEW_TABS.map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => handleTabChange(key)}
                className={filterClasses(
                  "h-full w-28 cursor-pointer transition-colors duration-100",
                  tab === key ? "text-grey-900" : "text-grey-600 hover:text-grey-900"
                )}
              >
                {label}
              </button>
            ))}
            <div
              className={filterClasses(
                "absolute bottom-0 left-3 h-[3px] w-28 bg-orange-500 transition-transform duration-300 ease-in-out",
                isEventsTab ? "translate-x-0" : "translate-x-28"
              )}
            />
          </div>
          {/* filters and actions. The rows alternate grey / white / grey / white down the column
              (Chuck, 2026-10-22): view strip, filter bar, sequence buttons, content. */}
          <div className="flex h-[55px] items-center justify-between border-b border-grey-400 bg-white px-3 py-2.5">
            <div className="flex items-center gap-2">
              <SearchInput
                placeholder="User: name, phone or #id"
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                onClear={() => setUserInput("")}
              />
              {/* localDay bounds — the location's own business days, no timezone involved.
                  The Suppressions view is a state view with no date dimension, so the inputs are
                  not shown there rather than greyed (Chuck, 2026-10-22). */}
              {isEventsTab && (
                <>
                  <input
                    type="date"
                    title="From (location-local day)"
                    value={from}
                    onChange={(e) => {
                      setFrom(e.target.value);
                      resetPagingAndSelection();
                    }}
                    className="h-9 rounded border border-grey-200 bg-white px-2 text-sm text-grey-900"
                  />
                  <span className="text-sm text-grey-500">–</span>
                  <input
                    type="date"
                    title="To (location-local day)"
                    value={to}
                    onChange={(e) => {
                      setTo(e.target.value);
                      resetPagingAndSelection();
                    }}
                    className="h-9 rounded border border-grey-200 bg-white px-2 text-sm text-grey-900"
                  />
                </>
              )}
              <FilterDropdown
                filterOptions={filterGroups}
                selectedFilters={selectedFilters}
                onChangeFilters={handleFilterChange}
                onClearAllFilters={handleClearFilters}
              />
            </div>
            <div className="flex items-center gap-3">
              {/* Display depth — only Swivl staff may switch to admin (ids + engine actions). */}
              {Member.isSwivlStaff && (
                <div className="flex items-center gap-1 rounded border border-grey-200 bg-white p-0.5 text-sm">
                  {(["agent", "admin"] as SuppressionsViewLevel[]).map((level) => (
                    <button
                      key={level}
                      type="button"
                      onClick={() => handleViewLevelChange(level)}
                      className={filterClasses(
                        "cursor-pointer rounded px-2 py-1 font-semibold capitalize",
                        viewLevel === level ? "bg-grey-900 text-white" : "text-grey-600 hover:text-grey-900"
                      )}
                    >
                      {level}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* filter chips section */}
          <ActiveFilterChipSection
            classNames={{ wrapper: "border-b border-grey-400" }}
            filterOptions={filterGroups}
            selectedFiltersByGroup={selectedFilters}
            handleClearFilter={(groupKey?: string) => handleFilterChange(groupKey ?? "", [])}
            handleClearFilters={handleClearFilters}
            handleSetFilters={handleFilterChange}
            variant={ActiveFilterChipSectionVariant.INLINE}
          />

          {/* sequence segments. The badges follow the view: event counts under the shared filters
              on Events, currently-held enrollment counts on Suppressions. */}
          <div className="flex flex-wrap items-center gap-2 border-b border-grey-400 bg-grey-50 px-3 py-2">
            {SEQUENCE_ORDER.map((type) => (
              <Button
                key={type}
                size={ButtonSize.SMALL}
                variant={sequenceType === type ? ButtonVariant.PRIMARY : ButtonVariant.GHOST}
                onClick={() => handleSequenceTypeChange(type)}
              >
                {SEQUENCE_LABELS[type]}
                <span className="ml-2 rounded-full bg-grey-100 px-2 py-0.5 text-xs text-grey-700">
                  {(isEventsTab ? stats?.byType?.[type] : suppressions?.byType?.[type]) ?? 0}
                </span>
              </Button>
            ))}
          </div>

          {/* content */}
          <div className="min-h-0 flex-1 overflow-y-auto bg-white p-3">
            <p className="mb-3 text-xs text-grey-500">
              {isEventsTab
                ? "Every scheduling decision the Sequences engine recorded — upcoming, sent, and not sent alike. Select the Not sent tile for the breakdown by reason."
                : "Enrollments on hold right now, and until when. Who applied a suppression, and why, is not recorded today."}{" "}
              Times are shown in {timezoneLabel}; the day column is each location&apos;s own business day, shown verbatim.
            </p>

            {isEventsTab ? (
              <div className="flex flex-col gap-3">
                <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
                  {STATUS_TILES.map((tile) => {
                    const count =
                      tile.statuses === null
                        ? Object.values(stats?.byStatus ?? {}).reduce((sum, value) => sum + value, 0)
                        : tile.statuses.reduce((sum, status) => sum + (stats?.byStatus?.[status] ?? 0), 0);
                    // Total is the view when no status is chosen, so it reads as selected then.
                    const isActive = tile.statuses === null ? statuses.length === 0 : sameStatuses(tile.statuses, statuses);
                    return (
                      <button
                        key={tile.key}
                        type="button"
                        onClick={() => handleTileClick(tile.statuses)}
                        className={filterClasses(
                          "flex cursor-pointer flex-col items-start rounded-lg border p-3 text-left transition-colors",
                          isActive ? "border-orange-500 bg-orange-50" : "border-grey-200 bg-white hover:border-grey-400 hover:bg-grey-50"
                        )}
                      >
                        <span className={filterClasses("text-xs tracking-wide uppercase", isActive ? "text-orange-500" : "text-grey-500")}>
                          {tile.label}
                        </span>
                        <span className="text-2xl font-semibold">{count}</span>
                      </button>
                    );
                  })}
                </div>

                {showByReason && (
                  <ByReasonCard stats={reasonStats} activeOutcome={drill.outcome} activeReason={drill.reason} onDrill={handleReasonDrill} />
                )}

                <section className="flex flex-col rounded-lg border border-grey-200 bg-white">
                  {events.length === 0 && !loading ? (
                    <p className="p-4 text-sm text-grey-500">No sequence events for {SEQUENCE_LABELS[sequenceType]} in this period.</p>
                  ) : (
                    <DataTable
                      variant={DataTableVariant.SECONDARY}
                      loading={loading}
                      columns={columns}
                      data={rows}
                      areRowsSelectable
                      handleSelectCell={(id) => setSelectedEventId(id !== undefined && id !== null ? Number(id) : null)}
                      selectedRowId={selectedEventId !== null ? String(selectedEventId) : null}
                      sorting={sorting}
                      onSortingChange={handleSortingChange}
                      manualConfig={{ manualSorting: true, manualPagination: true, manualFiltering: true, useCursorPagination: true }}
                      paginationState={{
                        hasNext: feedPage < lastPage,
                        hasPrev: feedPage > 1,
                        onNext: () => setEventsPage(feedPage + 1),
                        onPrev: () => setEventsPage(feedPage - 1)
                      }}
                    />
                  )}
                  <div className="border-t border-grey-100 px-4 py-2 text-sm text-grey-600">{pagerLabel(feedPage, pageSize, total)}</div>
                </section>
              </div>
            ) : (
              <SuppressionsTab
                sequenceType={sequenceType}
                data={suppressions}
                loading={loading}
                selectedEnrollmentId={selectedEnrollmentId}
                sorting={suppressionsSorting}
                onSortingChange={handleSuppressionsSortingChange}
                onPageChange={setSuppressionsPage}
                onSelectSuppression={setSelectedEnrollmentId}
              />
            )}
          </div>
        </div>
      </div>

      <Panel
        isOpen={!!selectedEvent || !!selectedSuppression}
        onToggle={() => {
          setSelectedEventId(null);
          setSelectedEnrollmentId(null);
        }}
      >
        {selectedEvent && <SequenceEventDetailPanel event={selectedEvent} viewLevel={viewLevel} onViewSuppressions={handleViewUserNotSent} />}
        {selectedSuppression && (
          <SuppressionsDetailPanel event={null} suppression={selectedSuppression} viewLevel={viewLevel} onViewUserEvents={handleViewUserNotSent} />
        )}
      </Panel>
    </>
  );
}
